import { useState, useCallback } from 'react';
import { apiFetch } from '../api';
import { nowCDT, dateToCDT } from '../dateUtils';

function GoalsHistory({ goalsData, onUpdate }) {
  const [selectedPerson, setSelectedPerson] = useState('Tim');
  const [weeksBack, setWeeksBack] = useState(0);
  const [pendingToggles, setPendingToggles] = useState({});

  const days = getDays();
  const personGoals = goalsData.goals.filter((g) => g.person === selectedPerson);
  const dailyGoals = personGoals.filter((g) => g.frequency === 'daily');
  const weeklyGoals = personGoals.filter((g) => g.frequency === 'weekly');

  function getDays() {
    const result = [];
    const now = nowCDT();
    const startOffset = weeksBack * 7 + 6;
    for (let i = startOffset; i >= weeksBack * 7; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      result.push({
        date: dateToCDT(d),
        label: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'America/Chicago' }),
        dayNum: d.getDate(),
        monthDay: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Chicago' }),
      });
    }
    return result;
  }

  function getWeekStart(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return dateToCDT(d);
  }

  const isCompleted = useCallback((goalId, dateKey) => {
    const toggleKey = `${goalId}_${dateKey}`;
    if (toggleKey in pendingToggles) {
      return pendingToggles[toggleKey];
    }
    return goalsData.completions.some(
      (c) => c.goalId === goalId && c.date === dateKey
    );
  }, [goalsData.completions, pendingToggles]);

  async function toggleGoal(goalId, dateKey) {
    const toggleKey = `${goalId}_${dateKey}`;
    const wasCompleted = isCompleted(goalId, dateKey);

    setPendingToggles((prev) => ({ ...prev, [toggleKey]: !wasCompleted }));

    try {
      await apiFetch('/api/goals/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goalId, date: dateKey }),
      });
      await onUpdate();
      setPendingToggles((prev) => {
        const next = { ...prev };
        delete next[toggleKey];
        return next;
      });
    } catch (err) {
      setPendingToggles((prev) => {
        const next = { ...prev };
        delete next[toggleKey];
        return next;
      });
      console.error('Failed to toggle goal:', err);
    }
  }

  const weekLabel = weeksBack === 0 ? 'This Week' : weeksBack === 1 ? 'Last Week' : `${weeksBack} Weeks Ago`;

  return (
    <section className="card goals-history-section">
      <h2>Goals History</h2>
      <div className="goals-history-controls">
        <div className="person-tabs">
          {['Tim', 'Emilio'].map((name) => (
            <button
              key={name}
              className={`person-tab${selectedPerson === name ? ' active' : ''}`}
              onClick={() => setSelectedPerson(name)}
            >
              {name}
            </button>
          ))}
        </div>
        <div className="week-nav">
          <button className="week-nav-btn" onClick={() => setWeeksBack((w) => w + 1)}>&lsaquo;</button>
          <span className="week-label">{weekLabel}</span>
          <button
            className="week-nav-btn"
            onClick={() => setWeeksBack((w) => Math.max(0, w - 1))}
            disabled={weeksBack === 0}
          >
            &rsaquo;
          </button>
        </div>
      </div>

      {dailyGoals.length > 0 && (
        <div className="goals-history-grid-wrapper">
          <h3>Daily Goals</h3>
          <div className="goals-history-grid" style={{ gridTemplateColumns: `120px repeat(${days.length}, 1fr)` }}>
            <div className="grid-header grid-label" />
            {days.map((day) => (
              <div key={day.date} className="grid-header">
                <span className="grid-day-name">{day.label}</span>
                <span className="grid-day-num">{day.dayNum}</span>
              </div>
            ))}
            {dailyGoals.map((goal) => (
              <HistoryGoalRow
                key={goal.id}
                goal={goal}
                days={days}
                isCompleted={isCompleted}
                onToggle={toggleGoal}
                getDateKey={(day) => day.date}
              />
            ))}
          </div>
        </div>
      )}

      {weeklyGoals.length > 0 && (
        <div className="goals-history-grid-wrapper">
          <h3>Weekly Goals</h3>
          <div className="goals-history-weekly">
            {weeklyGoals.map((goal) => {
              const weekStart = getWeekStart(days[0].date);
              const completed = isCompleted(goal.id, weekStart);
              return (
                <div key={goal.id} className={`weekly-goal-row${completed ? ' completed' : ''}`}>
                  <button
                    className="goal-check"
                    onClick={() => toggleGoal(goal.id, weekStart)}
                  >
                    {completed ? '\u2705' : '\u2B1C'}
                  </button>
                  <span className="goal-title">{goal.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {personGoals.length === 0 && (
        <p className="empty">No goals set for {selectedPerson}</p>
      )}
    </section>
  );
}

function HistoryGoalRow({ goal, days, isCompleted, onToggle, getDateKey }) {
  return (
    <>
      <div className="grid-goal-name">{goal.title}</div>
      {days.map((day) => {
        const dateKey = getDateKey(day);
        const done = isCompleted(goal.id, dateKey);
        return (
          <div
            key={day.date}
            className={`grid-cell history-cell ${done ? 'done' : 'missed'}`}
            onClick={() => onToggle(goal.id, dateKey)}
            title={`${day.monthDay} - Click to ${done ? 'unmark' : 'mark'} complete`}
          >
            {done ? '\u2705' : '\u2796'}
          </div>
        );
      })}
    </>
  );
}

export default GoalsHistory;
